import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Activity, Users, Truck, DollarSign,
  Briefcase, ArrowUpRight, ArrowDownRight, Download, ShieldCheck, Shield, Send
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { trpc } from '@/trpc/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer
} from 'recharts';
import { formatDistanceToNow } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CreateJob from "../jobs/CreateJob"; // Reusing the client component for Broker Mode
// Assuming SOSLogs is a new simple component we might need or reusing table
import AnalyticsDashboard from "@/components/analytics/AnalyticsDashboard";

// --- Components ---

const StatCard = ({
  title,
  value,
  icon: Icon,
  subtext,
  chartData
}: {
  title: string;
  value: string | number;
  icon: any;
  subtext?: string;
  chartData?: any[];
}) => {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between space-y-0 pb-2">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex items-end justify-between">
          <div>
            <div className="text-2xl font-bold">{value}</div>
            {subtext && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <span className="opacity-70">{subtext}</span>
              </p>
            )}
          </div>
          {chartData && chartData.length > 0 && (
            <div className="h-12 w-24">
              <LineChart width={96} height={48} data={chartData}>
                <Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} dot={false} />
              </LineChart>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const AdminDashboardPage: React.FC = () => {
  const [testNotificationStatus, setTestNotificationStatus] = useState<{
    message?: string;
    type?: 'success' | 'error';
  }>({});

  // Fetch stats with polling for real-time updates
  const { data: stats, isLoading: isLoadingStats, error } = trpc.admin.getSystemStats.useQuery(undefined, {
    staleTime: 5000,
    refetchInterval: 5000
  });

  const { data: revenueData } = trpc.admin.getRevenueStats.useQuery(undefined, {
    refetchInterval: 5000
  });

  // Fetch user growth stats
  const { data: userGrowthData } = trpc.admin.getUserGrowthStats.useQuery(undefined, {
    refetchInterval: 5000
  });

  // Fetch audit logs
  const { data: auditLogs } = trpc.admin.getAuditLogs.useQuery(
    { page: 1, pageSize: 5 },
    { refetchInterval: 5000 }
  );

  // Test notification mutation
  const sendTestNotificationMutation = trpc.admin.sendTestNotification.useMutation({
    onSuccess: (data) => {
      setTestNotificationStatus({
        message: data.message,
        type: 'success'
      });
      setTimeout(() => setTestNotificationStatus({}), 5000);
    },
    onError: (error) => {
      setTestNotificationStatus({
        message: error.message || 'Failed to send test notification',
        type: 'error'
      });
      setTimeout(() => setTestNotificationStatus({}), 5000);
    }
  });

  // Report generation
  const { refetch: fetchReport, isFetching: isGeneratingReport } = trpc.admin.getSystemReport.useQuery(undefined, {
    enabled: false,
    retry: false
  });

  const handleDownloadReport = async () => {
    try {
      const result = await fetchReport();
      if (!result.data) return;

      const report = result.data;

      // Convert to CSV
      const rows = [
        ['System Report', new Date().toISOString()],
        [''],
        ['SUMMARY'],
        ['Total Users', report.summary.totalUsers],
        ['Total Clients', report.summary.totalClients],
        ['Total Transporters', report.summary.totalTransporters],
        ['Verified Transporters', report.summary.verifiedTransporters],
        ['Total Vehicles', report.summary.totalVehicles],
        ['Verified Vehicles', report.summary.verifiedVehicles],
        ['Active Jobs', report.summary.activeJobs],
        ['Completed Jobs', report.summary.completedJobs],
        ['Total Bookings', report.summary.totalBookings],
        ['Total Revenue (MWK)', report.summary.totalRevenue],
        [''],
        ['RECENT ACTIVITY'],
        ['Timestamp', 'User', 'Role', 'Action', 'Status']
      ];

      report.recentActivity.forEach(log => {
        rows.push([
          new Date(log.timestamp).toLocaleString(),
          log.user,
          log.role,
          log.action,
          log.status
        ]);
      });

      const csvContent = "data:text/csv;charset=utf-8,"
        + rows.map(e => e.join(",")).join("\n");

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `system_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      console.error('Failed to generate report:', err);
    }
  };

  const isLoading = isLoadingStats;

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <Shield className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isLoading || !stats) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Calculate vehicle status distribution
  const vehicleStatusData = [
    { name: 'Verified', value: stats.verifiedVehicles, color: '#22c55e' },
    { name: 'Pending', value: stats.totalVehicles - stats.verifiedVehicles, color: '#eab308' },
  ].filter(item => item.value > 0);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Test Notification Status Alert */}
      {testNotificationStatus.message && (
        <Alert variant={testNotificationStatus.type === 'error' ? 'destructive' : 'default'}>
          <Send className="h-4 w-4" />
          <AlertTitle>
            {testNotificationStatus.type === 'success' ? 'Success' : 'Error'}
          </AlertTitle>
          <AlertDescription>{testNotificationStatus.message}</AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>
          <p className="text-muted-foreground">Overview of system performance and key metrics.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => sendTestNotificationMutation.mutate()}
            disabled={sendTestNotificationMutation.isLoading}
          >
            <Send className="mr-2 h-4 w-4" />
            {sendTestNotificationMutation.isLoading ? 'Sending...' : 'Test Notification'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadReport}
            disabled={isGeneratingReport}
          >
            <Download className="mr-2 h-4 w-4" />
            {isGeneratingReport ? 'Generating...' : 'Download Report'}
          </Button>
          <Button size="sm">
            <Activity className="mr-2 h-4 w-4" />
            Live View
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="broker">Broker Mode</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="sos">SOS Logs</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="space-y-4">

          {/* Key Metrics Row */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Revenue"
              value={`MWK ${(stats.totalRevenue / 1000).toFixed(1)}k`}
              icon={DollarSign}
              subtext="Lifetime revenue"
              chartData={revenueData?.map(d => ({ value: d.revenue }))}
            />
            <StatCard
              title="Active Jobs"
              value={stats.activeJobs}
              icon={Briefcase}
              subtext="Currently active"
            />
            <StatCard
              title="Total Users"
              value={stats.totalUsers}
              icon={Users}
              subtext={`${stats.totalTransporters} transporters, ${stats.totalClients} clients`}
              chartData={userGrowthData?.map(d => ({ value: d.users }))}
            />
            <StatCard
              title="Pending Verifications"
              value={stats.totalTransporters - stats.verifiedTransporters}
              icon={Shield}
              subtext="Transporters awaiting approval"
            />
          </div>

          {/* Charts Row */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
            {/* Main Revenue Chart */}
            <Card className="col-span-4">
              <CardHeader>
                <CardTitle>Revenue Overview</CardTitle>
                <CardDescription>Daily revenue for the last 7 days.</CardDescription>
              </CardHeader>
              <CardContent className="pl-2">
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={revenueData || []}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="name" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#888888" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `MWK${value}`} />
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                    <Tooltip formatter={(value) => [`MWK ${value}`, 'Revenue']} />
                    <Area type="monotone" dataKey="revenue" stroke="#8884d8" fillOpacity={1} fill="url(#colorRevenue)" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Secondary Chart: Vehicle Status */}
            <Card className="col-span-3">
              <CardHeader>
                <CardTitle>Vehicle Status Distribution</CardTitle>
                <CardDescription>Current fleet verification status.</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={vehicleStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {vehicleStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-4">
                  {vehicleStatusData.map((item) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-sm text-muted-foreground">{item.name} ({item.value})</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity & Quick Actions */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card className="col-span-2">
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Latest system events and audit logs.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-8">
                  {auditLogs?.logs.map((log) => (
                    <div key={log.id} className="flex items-center">
                      <div className="space-y-1">
                        <p className="text-sm font-medium leading-none">
                          <span className="capitalize">{log.userRole ? log.userRole.toLowerCase() : 'unknown'}</span> <span className="text-muted-foreground font-normal">performed</span> {log.action.replace(/_/g, ' ')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  ))}
                  {(!auditLogs?.logs || auditLogs.logs.length === 0) && (
                    <p className="text-sm text-muted-foreground">No recent activity found.</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Common administrative tasks.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start" asChild>
                  <a href="/admin/verifications">
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    Verify New Vehicles
                  </a>
                </Button>
                <Button variant="outline" className="w-full justify-start" asChild>
                  <a href="/admin/users">
                    <Users className="mr-2 h-4 w-4" />
                    Manage Users
                  </a>
                </Button>
                <Button variant="outline" className="w-full justify-start" asChild>
                  <a href="/admin/jobs">
                    <Briefcase className="mr-2 h-4 w-4" />
                    Review Jobs
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="broker">
          <Card>
            <CardHeader>
              <CardTitle>Broker Mode</CardTitle>
              <CardDescription>Post jobs on behalf of clients or as a system broker.</CardDescription>
            </CardHeader>
            <CardContent>
              <CreateJob />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics">
          <AnalyticsDashboard />
        </TabsContent>

        <TabsContent value="sos">
          <Card>
            <CardHeader><CardTitle>SOS Emergency Logs</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                <ShieldCheck className="h-12 w-12 mb-4 opacity-50" />
                <p>SOS Logs Module Coming Soon</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboardPage;